import { useState } from 'react'
import { useStore } from '../../stores/useStore'
import { INSTRUMENT_TYPES, MODULE_PARAMETERS, PREFERRED_PARAMETERS, type PatchPoint } from '../../types'

export default function EffectsPanel() {
  const modules = useStore((state) => state.modules)
  const patches = useStore((state) => state.patches)
  const selectedModuleId = useStore((state) => state.selectedModuleId)
  const selectedModuleType = useStore((state) => state.selectedModuleType)
  const updateModuleParameter = useStore((state) => state.updateModuleParameter)
  const toggleModule = useStore((state) => state.toggleModule)
  const addPatch = useStore((state) => state.addPatch)
  const clearPatches = useStore((state) => state.clearPatches)

  const instrument = modules.find((m) => m.id === selectedModuleId && m.type === 'instrument') as { id: string; parameters: Record<string, number>; name: string; enabled: boolean } | undefined
  const effect = modules.find((m) => m.id === selectedModuleId && m.type === 'effect') as { id: string; parameters: Record<string, number>; name: string; enabled: boolean } | undefined
  const activeMod = selectedModuleType === 'instrument' ? instrument : effect
  const params = activeMod?.parameters ?? {}

  const [fromJack, setFromJack] = useState<PatchPoint | null>(null)

  const handleJackClick = (jack: PatchPoint) => {
    if (!fromJack) {
      setFromJack(jack)
    } else if (fromJack !== jack) {
      addPatch(fromJack, jack)
      setFromJack(null)
    } else {
      setFromJack(null)
    }
  }

  const cableColor = (color?: string) => color || '#ffd700'

  const renderParams = () =>
    PREFERRED_PARAMETERS.filter((key) => key in params).map((key) => {
      const meta = MODULE_PARAMETERS[key]
      return (
        <div key={key} className="param-knob">
          <label>{meta.label}</label>
          <input
            type="range"
            min={meta.min}
            max={meta.max}
            step={meta.step}
            value={params[key] ?? 0.5}
            onChange={(e) => updateModuleParameter(selectedModuleType, selectedModuleId!, key, parseFloat(e.target.value))}
            className="slider"
          />
          <span className="param-value">{(params[key] ?? 0.5).toFixed(2)}</span>
        </div>
      )
    })

  const jackPositions: { id: PatchPoint; x: number; y: number }[] = [
    { id: 'Input', x: 30, y: 40 },
    { id: 'L', x: 30, y: 70 },
    { id: 'R', x: 60, y: 70 },
    { id: 'Filter Cut', x: 90, y: 40 },
    { id: 'Filter Res', x: 90, y: 70 },
    { id: 'Output', x: 150, y: 55 },
  ]

  return (
    <aside className="effects-panel">
      <div className="panel-header">
        <h3>{selectedModuleType === 'instrument' ? 'Instruments' : 'Effects'}</h3>
      </div>

      <div className="panel-content">
        <div className="section">
          <h4>Instruments</h4>
          <div className="instrument-list">
            {INSTRUMENT_TYPES.map((type) => {
              const mod = modules.find((m) => m.type === 'instrument' && (m as import('../../types').Instrument).instrumentType === type)
              const isSelected = mod && mod.id === selectedModuleId && selectedModuleType === 'instrument'
              return (
                <button
                  key={type}
                  className={`btn ${isSelected ? 'btn-primary' : ''}`}
                  onClick={() => {
                    if (!mod) {
                      useStore.setState((s) => ({
                        modules: [
                          ...s.modules,
                          {
                            id: `inst-${type.toLowerCase()}-${Date.now()}`,
                            name: type,
                            type: 'instrument',
                            instrumentType: type,
                            enabled: true,
                            selected: true,
                            parameters: { attack: 0.05, release: 0.25, depth: 0.45, rate: 0.2, freq: 0.5, reso: 0.25, damp: 0.2, wet: 0.15, delay: 0.25, width: 0.5, threshold: 0.7, ratio: 0.6 },
                          },
                        ],
                        selectedModuleId: '',
                        selectedModuleType: 'instrument',
                      }))
                    } else {
                      useStore.getState().selectModule('instrument', mod.id)
                    }
                  }}
                >
                  {type}
                </button>
              )
            })}
          </div>
        </div>

        <div className="section">
          <h4>Effects</h4>
          <div className="effect-list">
            {['reverb', 'delay', 'filter', 'distortion', 'chorus', 'limiter'].map((fxType) => {
              const mod = modules.find((m) => m.type === 'effect' && (m as import('../../types').Effect).effectType === fxType)
              const isSelected = mod && mod.id === selectedModuleId && selectedModuleType === 'effect'
              return (
                <button
                  key={fxType}
                  className={`btn ${isSelected ? 'btn-primary' : ''}`}
                  onClick={() => {
                    if (!mod) {
                      useStore.setState((s) => ({
                        modules: [
                          ...s.modules,
                          {
                            id: `fx-${fxType}-${Date.now()}`,
                            name: fxType,
                            type: 'effect',
                            effectType: fxType,
                            enabled: true,
                            selected: true,
                            parameters: { depth: 0.4, rate: 0.2, freq: 0.5, reso: 0.3, damp: 0.25, wet: 0.2, delay: 0.3, width: 0.5, attack: 0.05, release: 0.25, threshold: 0.75, ratio: 0.6 },
                          },
                        ],
                        selectedModuleId: '',
                        selectedModuleType: 'effect',
                      }))
                    } else {
                      useStore.getState().selectModule('effect', mod.id)
                    }
                  }}
                >
                  {fxType}
                </button>
              )
            })}
          </div>
        </div>

        {activeMod && (
          <div className="parameter-controls">
            <h4>{activeMod.name}</h4>
            {renderParams()}
            <div style={{ marginTop: '12px' }}>
              <button
                className={`btn ${activeMod.enabled ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => toggleModule(activeMod.id)}
              >
                {activeMod.enabled ? 'Bypass' : 'Enable'}
              </button>
            </div>
          </div>
        )}

        <div className="patch-bay">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h4 style={{ margin: 0 }}>Patch Bay</h4>
            <button className="btn" onClick={clearPatches} style={{ fontSize: '11px' }}>Clear</button>
          </div>
          <svg viewBox="0 0 180 120" style={{ background: '#1a1a1a', borderRadius: '4px' }}>
            {jackPositions.map((jack) => {
              const hasCable = fromJack === jack.id || patches.some((p) => p.from === jack.id || p.to === jack.id)
              return (
                <g key={jack.id} onClick={() => handleJackClick(jack.id)} style={{ cursor: 'pointer' }}>
                  <circle cx={jack.x} cy={jack.y} r="6" fill={hasCable ? '#ffd700' : '#373737'} stroke="#118C9B" strokeWidth="1.5" />
                  <text x={jack.x + 10} y={jack.y + 4} fontSize="9" fill="#cbcbcb" fontWeight="600">{jack.id}</text>
                </g>
              )
            })}
            {patches.map((p) => {
              const from = jackPositions.find((j) => j.id === p.from)
              const to = jackPositions.find((j) => j.id === p.to)
              if (!from || !to) return null
              return (
                <line key={p.id} x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke={cableColor(p.color)} strokeWidth="2" />
              )
            })}
            {fromJack && (
              <line x1={jackPositions.find((j) => j.id === fromJack)?.x ?? 0} y1={jackPositions.find((j) => j.id === fromJack)?.y ?? 0} x2="90" y2="60" stroke="#ffffff44" strokeWidth="1" strokeDasharray="3,2" />
            )}
          </svg>
        </div>
      </div>
    </aside>
  )
}
